import React, { useState, useEffect, useMemo } from 'react'
import { api } from '../../utils/api'
import { useToast } from '../../hooks/useToast'
import {
  defaultParams,
  calculateCapacity,
  buildTimeline,
  findCapacityReached,
  calculateSpaceNeeded,
} from '../../utils/warehouseCapacity'
import './WarehouseCapacityCalculator.css'

const API = '/api/warehouse-capacity/scenarios'
const fmt = (n) => Number(n || 0).toLocaleString()

// Merge a stored (possibly older/partial) params blob over the defaults so the
// UI never reads undefined nested fields.
function hydrate(stored) {
  const d = defaultParams()
  if (!stored || typeof stored !== 'object') return d
  return {
    ...d, ...stored,
    f1aLong: { ...d.f1aLong, ...stored.f1aLong },
    f1b: { ...d.f1b, ...stored.f1b },
    floor2: {
      ...d.floor2, ...stored.floor2,
      toteSmall: { ...d.floor2.toteSmall, ...stored.floor2?.toteSmall },
      toteMedium: { ...d.floor2.toteMedium, ...stored.floor2?.toteMedium },
    },
    projections: Array.isArray(stored.projections) && stored.projections.length
      ? stored.projections
      : d.projections,
  }
}

// Controlled numeric input that keeps '' while editing (calc coerces '' → 0).
function NumField({ label, value, onChange }) {
  return (
    <label className="wc-field">
      <span>{label}</span>
      <input
        type="number" min="0" value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    </label>
  )
}

export default function WarehouseCapacityCalculator() {
  const { toast } = useToast()
  const [params, setParams] = useState(defaultParams)
  const [scenarios, setScenarios] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [name, setName] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => { loadScenarios() }, [])

  async function loadScenarios() {
    try {
      const res = await api.get(API)
      setScenarios(res.scenarios || [])
    } catch (err) {
      toast.error(`Couldn't load scenarios: ${err.message}`)
    }
  }

  // ---- derived (live recompute) ----
  const caps = useMemo(() => calculateCapacity(params), [params])
  const timeline = useMemo(() => buildTimeline(params.projections, caps.totalCapacity), [params.projections, caps.totalCapacity])
  const reached = useMemo(() => findCapacityReached(params.projections, caps.totalCapacity), [params.projections, caps.totalCapacity])
  const space = useMemo(
    () => calculateSpaceNeeded(params.projections, caps.itemsPerSqFt, params.currentSqFt),
    [params.projections, caps.itemsPerSqFt, params.currentSqFt]
  )

  // ---- param mutation helpers ----
  const setSection = (section, key, value) =>
    setParams((p) => ({ ...p, [section]: { ...p[section], [key]: value } }))
  const setTote = (size, key, value) =>
    setParams((p) => ({ ...p, floor2: { ...p.floor2, [size]: { ...p.floor2[size], [key]: value } } }))
  const setTop = (key, value) => setParams((p) => ({ ...p, [key]: value }))

  const setProjection = (idx, key, value) =>
    setParams((p) => ({ ...p, projections: p.projections.map((r, i) => (i === idx ? { ...r, [key]: value } : r)) }))
  const addProjection = () =>
    setParams((p) => ({ ...p, projections: [...p.projections, { label: 'New period', items: 0 }] }))
  const removeProjection = (idx) =>
    setParams((p) => ({ ...p, projections: p.projections.filter((_, i) => i !== idx) }))

  // ---- scenario actions ----
  function onSelect(e) {
    const id = e.target.value
    setSelectedId(id)
    setConfirmingDelete(false)
    if (!id) { setParams(defaultParams()); setName(''); return }
    const s = scenarios.find((x) => String(x.scenario_id) === String(id))
    if (s) { setParams(hydrate(s.params)); setName(s.name) }
  }

  function newScenario() {
    setSelectedId('')
    setName('')
    setParams(defaultParams())
    setConfirmingDelete(false)
  }

  // Upsert by name: updates the scenario that already has this name, else creates one.
  async function save() {
    const trimmed = name.trim()
    if (!trimmed) { toast.error('Enter a scenario name first'); return }
    try {
      const existing = scenarios.find((s) => s.name === trimmed)
      if (existing) {
        await api.put(`${API}/${existing.scenario_id}`, { name: trimmed, params })
        await loadScenarios()
        setSelectedId(String(existing.scenario_id))
      } else {
        const res = await api.post(API, { name: trimmed, params })
        await loadScenarios()
        if (res.scenario_id) setSelectedId(String(res.scenario_id))
      }
      toast.success(`Saved "${trimmed}"`)
    } catch (err) {
      toast.error(`Save failed: ${err.message}`)
    }
  }

  async function doDelete() {
    try {
      await api.delete(`${API}/${selectedId}`)
      toast.success('Scenario deleted')
      setConfirmingDelete(false)
      newScenario()
      loadScenarios()
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`)
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-header">
        <div>
          <h1>Warehouse Capacity Calculator</h1>
          <p className="text-muted">
            Shelving (F1A &amp; F1B): bays × levels × items per level. Totes (F2): bays × levels × totes × items per tote.
          </p>
        </div>
      </div>

      {/* Scenario bar */}
      <div className="card" style={{ padding: 'var(--space-3, 16px)', marginBottom: 'var(--space-4, 20px)' }}>
        <div className="wc-scenario-bar">
          <label style={{ fontWeight: 600 }}>Scenario:</label>
          <select className="form-input" value={selectedId} onChange={onSelect}>
            <option value="">-- New (unsaved) --</option>
            {scenarios.map((s) => (
              <option key={s.scenario_id} value={s.scenario_id}>{s.name}</option>
            ))}
          </select>

          <input
            className="form-input" placeholder="Scenario name" value={name} maxLength={80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            style={{ minWidth: 200 }}
          />
          <button className="btn btn-primary" onClick={save} disabled={!name.trim()}>Save</button>
          <button className="btn btn-ghost" onClick={newScenario}>New</button>
          {selectedId && !confirmingDelete && (
            <button className="btn btn-ghost" onClick={() => setConfirmingDelete(true)}>Delete</button>
          )}
          {selectedId && confirmingDelete && (
            <>
              <button className="btn btn-danger" onClick={doDelete}>Confirm delete</button>
              <button className="btn btn-ghost" onClick={() => setConfirmingDelete(false)}>Cancel</button>
            </>
          )}
        </div>
        <p className="wc-subtitle" style={{ marginTop: 8, marginBottom: 0 }}>
          Type a name and hit <strong>Save</strong> — saving an existing name updates it; a new name creates a new scenario. <strong>New</strong> starts a blank one.
        </p>
      </div>

      {/* Params */}
      <div className="wc-grid">
        {/* Floor 1 */}
        <div className="card" style={{ padding: 'var(--space-4, 20px)' }}>
          <h3 className="wc-section-title"><span>📦</span> Floor 1</h3>

          <div className="wc-subtitle">Longspan (deep racking)</div>
          <div className="wc-dims">
            <NumField label="Width (mm)" value={params.f1aLong.width} onChange={(v) => setSection('f1aLong', 'width', v)} />
            <NumField label="Height (mm)" value={params.f1aLong.height} onChange={(v) => setSection('f1aLong', 'height', v)} />
            <NumField label="Depth (mm)" value={params.f1aLong.depth} onChange={(v) => setSection('f1aLong', 'depth', v)} />
            <NumField label="Levels" value={params.f1aLong.levels} onChange={(v) => setSection('f1aLong', 'levels', v)} />
          </div>
          <div className="wc-row"><label>Number of bays</label>
            <input type="number" min="0" value={params.f1aLong.bays ?? ''} onChange={(e) => setSection('f1aLong', 'bays', e.target.value === '' ? '' : Number(e.target.value))} /></div>
          <div className="wc-row"><label>Items per level</label>
            <input type="number" min="0" value={params.f1aLong.itemsPerLevel ?? ''} onChange={(e) => setSection('f1aLong', 'itemsPerLevel', e.target.value === '' ? '' : Number(e.target.value))} /></div>
          <div className="wc-subtotal"><span>F1A Capacity</span><span className="wc-value">{fmt(caps.f1aCapacity)}</span></div>

          <div className="wc-divider" />

          <div className="wc-subtitle">Shelving</div>
          <div className="wc-dims">
            <NumField label="Width (mm)" value={params.f1b.width} onChange={(v) => setSection('f1b', 'width', v)} />
            <NumField label="Height (mm)" value={params.f1b.height} onChange={(v) => setSection('f1b', 'height', v)} />
            <NumField label="Depth (mm)" value={params.f1b.depth} onChange={(v) => setSection('f1b', 'depth', v)} />
            <NumField label="Levels" value={params.f1b.levels} onChange={(v) => setSection('f1b', 'levels', v)} />
          </div>
          <div className="wc-row"><label>Number of bays</label>
            <input type="number" min="0" value={params.f1b.bays ?? ''} onChange={(e) => setSection('f1b', 'bays', e.target.value === '' ? '' : Number(e.target.value))} /></div>
          <div className="wc-row"><label>Items per level</label>
            <input type="number" min="0" value={params.f1b.itemsPerLevel ?? ''} onChange={(e) => setSection('f1b', 'itemsPerLevel', e.target.value === '' ? '' : Number(e.target.value))} /></div>
          <div className="wc-subtotal"><span>F1B Capacity</span><span className="wc-value">{fmt(caps.f1bCapacity)}</span></div>

          <div className="wc-subtotal"><span>Floor 1 Capacity</span><span className="wc-value">{fmt(caps.floor1Capacity)}</span></div>
        </div>

        {/* Floor 2 */}
        <div className="card" style={{ padding: 'var(--space-4, 20px)' }}>
          <h3 className="wc-section-title"><span>🗃️</span> Floor 2 — Tote Racking</h3>
          <div className="wc-dims">
            <NumField label="Width (mm)" value={params.floor2.width} onChange={(v) => setSection('floor2', 'width', v)} />
            <NumField label="Height (mm)" value={params.floor2.height} onChange={(v) => setSection('floor2', 'height', v)} />
            <NumField label="Depth (mm)" value={params.floor2.depth} onChange={(v) => setSection('floor2', 'depth', v)} />
            <NumField label="Levels" value={params.floor2.levels} onChange={(v) => setSection('floor2', 'levels', v)} />
          </div>
          <div className="wc-row"><label>Number of bays</label>
            <input type="number" min="0" value={params.floor2.bays ?? ''} onChange={(e) => setSection('floor2', 'bays', e.target.value === '' ? '' : Number(e.target.value))} /></div>
          <div className="wc-row"><label>Totes per level</label>
            <input type="number" min="0" value={params.floor2.totesPerLevel ?? ''} onChange={(e) => setSection('floor2', 'totesPerLevel', e.target.value === '' ? '' : Number(e.target.value))} /></div>
          <div className="wc-row"><label>Items per tote</label>
            <input type="number" min="0" value={params.floor2.itemsPerTote ?? ''} onChange={(e) => setSection('floor2', 'itemsPerTote', e.target.value === '' ? '' : Number(e.target.value))} /></div>

          <div className="wc-subtitle" style={{ marginTop: 12 }}>Tote sizes (cm) — reference only</div>
          <div className="wc-dims">
            <NumField label="Small L" value={params.floor2.toteSmall.l} onChange={(v) => setTote('toteSmall', 'l', v)} />
            <NumField label="Small W" value={params.floor2.toteSmall.w} onChange={(v) => setTote('toteSmall', 'w', v)} />
            <NumField label="Small H" value={params.floor2.toteSmall.h} onChange={(v) => setTote('toteSmall', 'h', v)} />
            <span />
          </div>
          <div className="wc-dims">
            <NumField label="Med L" value={params.floor2.toteMedium.l} onChange={(v) => setTote('toteMedium', 'l', v)} />
            <NumField label="Med W" value={params.floor2.toteMedium.w} onChange={(v) => setTote('toteMedium', 'w', v)} />
            <NumField label="Med H" value={params.floor2.toteMedium.h} onChange={(v) => setTote('toteMedium', 'h', v)} />
            <span />
          </div>

          <div className="wc-subtotal"><span>Total totes</span><span className="wc-value">{fmt(caps.totalTotesNeeded)}</span></div>
          <div className="wc-subtotal"><span>Floor 2 Capacity</span><span className="wc-value">{fmt(caps.floor2Capacity)}</span></div>
        </div>
      </div>

      {/* Total */}
      <div className="wc-total">
        <h3>TOTAL WAREHOUSE CAPACITY</h3>
        <div className="wc-big">{fmt(caps.totalCapacity)}</div>
        <div className="wc-sub">items</div>
      </div>

      {/* Current warehouse */}
      <div className="card" style={{ padding: 'var(--space-4, 20px)', marginBottom: 'var(--space-4, 20px)' }}>
        <h3 className="wc-section-title"><span>📐</span> Current Warehouse</h3>
        <div className="wc-row"><label>Usable square feet</label>
          <input type="number" min="0" value={params.currentSqFt ?? ''} onChange={(e) => setTop('currentSqFt', e.target.value === '' ? '' : Number(e.target.value))} /></div>
        <div className="wc-row"><label>Items per sq ft</label><span className="wc-value" style={{ color: 'var(--green-onions, #3BB273)', fontWeight: 700 }}>{caps.itemsPerSqFt.toFixed(2)}</span></div>
      </div>

      {/* Projections */}
      <div className="card" style={{ padding: 'var(--space-4, 20px)', marginBottom: 'var(--space-4, 20px)' }}>
        <h3 className="wc-section-title"><span>📈</span> Growth Projections</h3>
        {params.projections.map((row, idx) => (
          <div className="wc-proj-row" key={idx}>
            <label className="wc-field"><span>Label</span>
              <input value={row.label} maxLength={40} onChange={(e) => setProjection(idx, 'label', e.target.value)} style={{ textAlign: 'left' }} /></label>
            <NumField label="Items" value={row.items} onChange={(v) => setProjection(idx, 'items', v)} />
            <button className="btn btn-ghost" onClick={() => removeProjection(idx)} disabled={params.projections.length <= 1} title="Remove period">✕</button>
          </div>
        ))}
        <button className="btn btn-ghost" onClick={addProjection} disabled={params.projections.length >= 12}>+ Add period</button>
      </div>

      {/* Results */}
      <div className="wc-grid">
        <div className="card" style={{ padding: 'var(--space-4, 20px)' }}>
          <h3 className="wc-section-title">Capacity Timeline</h3>
          {timeline.map((t, idx) => (
            <div className="wc-tl-row" key={idx}>
              <span className="wc-tl-period">{t.label}</span>
              <span className="wc-tl-items">{fmt(t.items)}</span>
              <div className="wc-tl-bar-wrap"><div className={`wc-tl-bar ${t.fits ? 'fits' : 'over'}`} style={{ width: `${t.barWidth}%` }} /></div>
              <span className={`wc-tl-pct ${t.fits ? 'fits' : 'over'}`}>{t.utilization.toFixed(0)}%</span>
              <span className="wc-tl-status">{t.fits ? '✓' : '✗'}</span>
            </div>
          ))}
        </div>

        <div>
          <div className="wc-reached">
            <h3>CAPACITY REACHED</h3>
            <div className="wc-big">{reached ?? '—'}</div>
          </div>
          <div className="card" style={{ padding: 'var(--space-4, 20px)' }}>
            <h3 className="wc-section-title">Space Needed for End of Plan</h3>
            <div className="wc-space-row"><span>Current warehouse</span><span className="wc-value">{fmt(params.currentSqFt)} sq ft</span></div>
            <div className="wc-space-row"><span>Total needed</span><span className="wc-value">{fmt(space.sqFtNeeded)} sq ft</span></div>
            <div className="wc-space-row total"><span>Additional required</span>
              <span className="wc-value">+{fmt(space.additionalSqFt)} sq ft ({space.multiple.toFixed(1)}×)</span></div>
          </div>
        </div>
      </div>
    </div>
  )
}
